import { Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import FusePageSimple from '@fuse/core/FusePageSimple';
import FuseAnimate from '@fuse/core/FuseAnimate/FuseAnimate';
import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import TableOmniNew from 'allGlobalComponents/TableOmniNew';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import AudioPlayerModal from 'app/main/globalComponents/AudioPlayerModal';
import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { formatDate } from 'app/utility/ExportToExcelUtility';
import { globalDateFormat } from 'app/constants';
import { useSnackbar, useApi, useExport, usePagination, useDateRange } from 'app/hooks';

const useStyles = makeStyles(theme => ({
  content: {
    width: '100%',
    padding: '0 16px',
    marginTop: '10px'
  },
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(0, 2)
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center'
  },
  headerIcon: {
    marginRight: theme.spacing(1.5)
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  detailsContent: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3)
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.secondary.value || theme.palette.primary.main,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  fieldLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase'
  },
  fieldValue: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.text.primary,
    wordBreak: 'break-word'
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    paddingLeft: theme.spacing(1)
  },
  timelineItem: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    position: 'relative',
    paddingLeft: theme.spacing(2),
    borderLeft: `2px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1)
  },
  timelineDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: theme.palette.secondary.value || theme.palette.primary.main,
    position: 'absolute',
    left: -6,
    top: 6
  },
  timelineEvent: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    textTransform: 'capitalize'
  },
  timelineTime: {
    fontSize: '12px',
    color: theme.palette.text.secondary
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%'
  },
  viewText: {
    color: theme.palette.type === 'dark' ? '#9CA3AF' : '#6B7280',
    fontWeight: 500,
    fontSize: '1.2rem',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    '&:hover': {
      color: theme.palette.type === 'dark' ? '#D1D5DB' : '#374151',
      textDecoration: 'underline'
    }
  },
}));

const DIRECTION_OPTIONS = [
  { value: '', label: 'All Directions' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' }
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'invite_sent', label: 'Invite Sent' },
  { value: 'invite_received', label: 'Invite Received' },
  { value: 'ringing', label: 'Ringing' },
  { value: 'progress', label: 'Progress' },
  { value: 'answered', label: 'Answered' },
  { value: 'completed', label: 'Completed' },
  { value: 'ended', label: 'Ended' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'missed', label: 'Missed' }
];

const formatDuration = (sec) => {
  if (!sec && sec !== 0) return '-';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Feedback can arrive as plain text or as an object from the API
const formatFeedback = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return value.feedback || value.comment || value.text || '-';
  return String(value);
};

// Pill mappings as functions to normalize casing
const directionPillMapping = (value) => {
  const map = {
    inbound: { label: 'Inbound', color: '#0369a1', bgColor: '#e0f2fe' },
    outbound: { label: 'Outbound', color: '#7c2d12', bgColor: '#ffedd5' }
  };
  const key = value?.toLowerCase();
  return map[key] || { label: value ? value.charAt(0).toUpperCase() + value.slice(1) : '-' };
};

const statusPillMapping = (value) => {
  const map = {
    invite_sent: { label: 'Invite Sent', color: '#1E40AF', bgColor: '#DBEAFE' },
    invite_received: { label: 'Invite Received', color: '#5B21B6', bgColor: '#EDE9FE' },
    ringing: { label: 'Ringing', color: '#0369a1', bgColor: '#e0f2fe' },
    progress: { label: 'Progress', color: '#854d0e', bgColor: '#fef9c3' },
    answered: { label: 'Answered', color: '#166534', bgColor: '#dcfce7' },
    completed: { label: 'Completed', color: '#065F46', bgColor: '#D1FAE5' },
    ended: { label: 'Ended', color: '#6B7280', bgColor: '#F3F4F6' },
    canceled: { label: 'Canceled', color: '#991b1b', bgColor: '#fee2e2' },
    missed: { label: 'Missed', color: '#991b1b', bgColor: '#fee2e2' }
  };
  const key = value?.toLowerCase()?.replace(/-/g, '_');
  return map[key] || { label: value ? value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ') : '-' };
};

function CallCenterReport() {
  const classes = useStyles();
  const theme = useTheme();

  // User data
  const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
  const userId = userData.id;
  const maxTokenCount = userData.max_token_count;

  const isAdmin = maxTokenCount == -1;
  const [agents, setAgents] = useState([]);

  // Fetch agents list for admin filter
  useEffect(() => {
    if (!isAdmin) return;
    request({
      module: 'users',
      method: 'listing',
      payload: {
        columns: '*',
        sortby: 'ASC',
        orderby: 'id',
        where: 'displayed = $1',
        values: true,
        limit: 100,
        page: 0
      },
      onSuccess: (response) => {
        const list = response.data.data.list?.data || [];
        setAgents(list);
      },
      onError: () => { }
    });
  }, []);

  // Custom Hooks
  const { snackbar, showError, showWarning } = useSnackbar();
  const { isLoading, request } = useApi();
  const { isLoading: isLoadingDetails, request: requestDetails } = useApi();
  const { isExporting, handleExport } = useExport();
  const { currentParams, setCurrentParams, totalItems, setTotalItems, resetPage } = usePagination(10);
  const { startDate, endDate, handleDateChange: onDateRangeChange } = useDateRange(true);

  // Local State
  const [data, setData] = useState([]);
  const [filterValues, setFilterValues] = useState({
    direction: '',
    status: '',
    from: '',
    to: '',
    userId: ''
  });

  // Shared, waveform-based player (used elsewhere in the app too) —
  // downloads the recording as a full blob before decoding it, so its
  // duration comes from the complete file rather than streamed container
  // metadata, which is what was making the native <audio> seek bar report
  // the wrong length.
  const [recordingOpen, setRecordingOpen] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState('');
  const [recordingCallId, setRecordingCallId] = useState('');

  const openRecording = (url, callId) => {
    setRecordingUrl(url);
    setRecordingCallId(callId);
    setRecordingOpen(true);
  };

  const handleDownloadRecording = async (url, callId) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const extMatch = /\.([a-z0-9]+)(?:\?.*)?$/i.exec(url);
      const ext = extMatch ? extMatch[1] : 'ogg';
      const filename = `call_recording_${callId || 'unknown'}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.${ext}`;
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      showError('Failed to download recording');
    }
  };

  // Details modal state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(null);

  const buildPayload = useCallback(() => {
    const payload = {
      page: currentParams.page,
      limit: currentParams.limit,
      start_date: startDate,
      end_date: endDate
    };

    // Admin: send userId only if a specific agent is selected in filter
    // Non-admin: always send own userId
    if (isAdmin) {
      if (filterValues.userId) payload.userId = Number.parseInt(filterValues.userId);
    } else {
      payload.userId = userId;
    }

    if (filterValues.direction) payload.direction = filterValues.direction;
    if (filterValues.status) payload.status = filterValues.status;
    if (filterValues.from?.trim()) payload.from = filterValues.from.trim();
    if (filterValues.to?.trim()) payload.to = filterValues.to.trim();
    return payload;
  }, [currentParams, filterValues, startDate, endDate, userId, isAdmin]);

  useEffect(() => {
    request({
      module: 'pbx',
      method: 'pbxCallsList',
      payload: buildPayload(),
      onSuccess: (response) => {
        const res = response.data.data;
        setData(res.calls || []);
        setTotalItems(res.totalItems || 0);
      },
      onError: (message) => showError(message || 'Error fetching calls')
    });
  }, [currentParams, filterValues, startDate, endDate]);

  const handleDateChange = (range) => {
    onDateRangeChange(range);
    resetPage();
  };

  const handleFiltersApply = (filters) => {
    setFilterValues(filters);
    resetPage();
  };

  const handleFiltersClear = (filters) => {
    setFilterValues(filters);
    resetPage();
  };

  const handleRefresh = () => {
    setCurrentParams(prev => ({ ...prev }));
  };

  // View / Details handlers
  const handleViewClick = (row) => {
    setDetailsOpen(true);
    setDetailsData(null);

    const detailsPayload = { callId: Number.parseInt(row.id) };
    if (!isAdmin) detailsPayload.userId = userId;

    requestDetails({
      module: 'pbx',
      method: 'pbxCallsDetails',
      payload: detailsPayload,
      onSuccess: (response) => {
        setDetailsData(response.data.data.call);
      },
      onError: (message) => {
        showError(message || 'Error fetching call details');
        setDetailsOpen(false);
      }
    });
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setDetailsData(null);
  };

  const exportColumns = [
    { header: 'S.No', key: '_index' },
    { header: 'ID', key: 'id' },
    { header: 'Agent', key: 'agent_username', transform: (v) => v || '-' },
    { header: 'Direction', key: 'direction', transform: (v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : '-' },
    { header: 'From', key: 'from_number', transform: (v) => v || '-' },
    { header: 'To', key: 'to_number', transform: (v) => v || '-' },
    { header: 'Customer Number', key: 'customer_number', transform: (v) => v || '-' },
    { header: 'Contact Name', key: 'contact_name', transform: (v) => v || '-' },
    { header: 'Status', key: 'status', transform: (v) => v ? v.charAt(0).toUpperCase() + v.slice(1).replace(/-/g, ' ') : '-' },
    { header: 'Duration', key: 'duration_sec', transform: (v) => formatDuration(v) },
    { header: 'Talk Time', key: 'talk_time_sec', transform: (v) => formatDuration(v) },
    { header: 'Started At', key: 'started_at', transform: (v) => formatDate(v) },
    { header: 'Answered At', key: 'answered_at', transform: (v) => formatDate(v) },
    { header: 'Ended At', key: 'ended_at', transform: (v) => formatDate(v) },
    { header: 'Feedback', key: 'feedback', transform: (v) => formatFeedback(v) }
  ];

  const onExport = () => {
    const exportPayload = { ...buildPayload(), page: 0, limit: totalItems || 10000 };

    handleExport({
      module: 'pbx',
      method: 'pbxCallsList',
      payload: exportPayload,
      fileNamePrefix: 'call_center_report',
      sheetName: 'Call Center Report',
      getDataFromResponse: (res) => res.data.data.calls || [],
      columns: exportColumns,
      onError: (msg) => showWarning(msg || 'No data to export')
    });
  };

  const filterFields = [
    ...(isAdmin ? [{
      name: 'userId',
      label: 'Agent',
      type: 'select',
      options: [
        { value: '', label: 'All Agents' },
        ...agents.map(a => ({ value: String(a.id), label: a.username }))
      ],
      defaultValue: ''
    }] : []),
    {
      name: 'from',
      label: 'From Number',
      type: 'text',
      placeholder: 'e.g. 0308...',
      defaultValue: ''
    },
    {
      name: 'to',
      label: 'To Number',
      type: 'text',
      placeholder: 'e.g. 0321...',
      defaultValue: ''
    },
    {
      name: 'direction',
      label: 'Direction',
      type: 'select',
      options: DIRECTION_OPTIONS,
      defaultValue: ''
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: STATUS_OPTIONS,
      defaultValue: ''
    }
  ];

  const headCells = [
    { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
    {
      id: 'agent_username',
      numeric: false,
      disablePadding: false,
      label: 'Agent',
      tooltip: true,
      customRow: row => row.agent_username || '-'
    },
    {
      id: 'direction',
      numeric: false,
      disablePadding: false,
      label: 'Direction',
      pill: true,
      pillConfig: { mapping: directionPillMapping }
    },
    {
      id: 'from_number',
      numeric: false,
      disablePadding: false,
      label: 'From',
      tooltip: true,
      customRow: row => row.from_number || '-'
    },
    {
      id: 'to_number',
      numeric: false,
      disablePadding: false,
      label: 'To',
      tooltip: true,
      customRow: row => row.to_number || '-'
    },
    {
      id: 'customer_number',
      numeric: false,
      disablePadding: false,
      label: 'Customer #',
      msg: true,
      truncateLength: 20,
      customRow: row => row.customer_number || '-'
    },
    { id: 'contact_name', numeric: false, disablePadding: false, label: 'Contact', customRow: row => row.contact_name || '-' },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: 'Status',
      pill: true,
      pillConfig: { mapping: statusPillMapping }
    },
    { id: 'duration_sec', numeric: true, disablePadding: false, label: 'Duration', customRow: row => formatDuration(row.duration_sec) },
    { id: 'talk_time_sec', numeric: true, disablePadding: false, label: 'Talk Time', customRow: row => formatDuration(row.talk_time_sec) },
    { id: 'started_at', numeric: false, disablePadding: false, label: 'Started', dt: true },
    { id: 'ended_at', numeric: false, disablePadding: false, label: 'Ended', dt: true },
    {
      id: 'feedback',
      numeric: false,
      disablePadding: false,
      label: 'Feedback',
      msg: true,
      truncateLength: 30,
      customRow: row => formatFeedback(row.feedback)
    },
    { id: 'view', label: 'Details', disableSort: true },
    {
      id: 'recording',
      numeric: false,
      disablePadding: false,
      label: 'Recording',
      disableSort: true,
      customRow: row => row.recording_url ? (
        <span
          className={classes.viewText}
          onClick={(e) => {
            e.stopPropagation();
            openRecording(row.recording_url, row.id);
          }}
        >
          PLAY
        </span>
      ) : '-'
    }
  ];

  // Render a single field
  const renderField = (label, value) => (
    <div className={classes.field}>
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{value ?? '-'}</span>
    </div>
  );

  // Skeleton helpers
  const renderFieldSkeleton = (key) => (
    <div key={key} className={classes.field}>
      <Skeleton variant="text" width={80} height={14} animation="wave" />
      <Skeleton variant="text" width="70%" height={20} animation="wave" />
    </div>
  );

  const renderSectionSkeleton = (titleWidth, fieldCount, sectionKey) => (
    <div className={classes.section} key={sectionKey}>
      <Skeleton variant="text" width={titleWidth} height={20} animation="wave" />
      <div className={classes.fieldGrid}>
        {[...Array(fieldCount)].map((_, i) => renderFieldSkeleton(`${sectionKey}-${i}`))}
      </div>
    </div>
  );

  // Details Modal Content
  const detailsContent = (
    <div className={classes.detailsContent}>
      {isLoadingDetails || !detailsData ? (
        <>
          {renderSectionSkeleton(160, 6, 'info')}
          {renderSectionSkeleton(100, 6, 'numbers')}
          {renderSectionSkeleton(120, 4, 'timestamps')}
          <div className={classes.section}>
            <Skeleton variant="text" width={140} height={20} animation="wave" />
            <div className={classes.timeline}>
              {[...Array(4)].map((_, i) => (
                <div key={i} className={classes.timelineItem}>
                  <div className={classes.timelineDot} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                    <Skeleton variant="text" width={120} height={16} animation="wave" />
                    <Skeleton variant="text" width={180} height={14} animation="wave" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Call Info */}
          <div className={classes.section}>
            <span className={classes.sectionTitle}>Call Information</span>
            <div className={classes.fieldGrid}>
              {renderField('Call ID', detailsData.id)}
              {renderField('Agent', detailsData.agent_username)}
              {renderField('Direction', detailsData.direction
                ? detailsData.direction.charAt(0).toUpperCase() + detailsData.direction.slice(1)
                : '-')}
              {renderField('Status', detailsData.status
                ? detailsData.status.charAt(0).toUpperCase() + detailsData.status.slice(1).replace(/-/g, ' ')
                : '-')}
              {renderField('Duration', formatDuration(detailsData.duration_sec))}
              {renderField('Talk Time', formatDuration(detailsData.talk_time_sec))}
            </div>
          </div>

          {/* Numbers */}
          <div className={classes.section}>
            <span className={classes.sectionTitle}>Numbers</span>
            <div className={classes.fieldGrid}>
              {renderField('From Number', detailsData.from_number)}
              {renderField('To Number', detailsData.to_number)}
              {renderField('From Extension', detailsData.from_extension)}
              {renderField('To Extension', detailsData.to_extension)}
              {renderField('Customer Number', detailsData.customer_number)}
              {renderField('Contact Name', detailsData.contact_name)}
            </div>
          </div>

          {/* Timestamps */}
          <div className={classes.section}>
            <span className={classes.sectionTitle}>Timestamps</span>
            <div className={classes.fieldGrid}>
              {renderField('Started', detailsData.started_at ? moment(detailsData.started_at).format(globalDateFormat) : '-')}
              {renderField('Answered', detailsData.answered_at ? moment(detailsData.answered_at).format(globalDateFormat) : '-')}
              {renderField('Ended', detailsData.ended_at ? moment(detailsData.ended_at).format(globalDateFormat) : '-')}
              {renderField('Created', detailsData.dt ? moment(detailsData.dt).format(globalDateFormat) : '-')}
            </div>
          </div>

          {/* Recording */}
          {detailsData.recording_url && (
            <div className={classes.section}>
              <span className={classes.sectionTitle}>Recording</span>
              <span
                className={classes.viewText}
                onClick={() => openRecording(detailsData.recording_url, detailsData.id)}
              >
                PLAY
              </span>
            </div>
          )}

          {/* Call Journey */}
          {detailsData.journey && detailsData.journey.length > 0 && (
            <div className={classes.section}>
              <span className={classes.sectionTitle}>Call Journey</span>
              <div className={classes.timeline}>
                {detailsData.journey.map((attempt) => {
                  const resultColor = attempt.result === 'answered' || attempt.result === 'completed'
                    ? '#166534'
                    : '#991b1b';
                  const resultBg = attempt.result === 'answered' || attempt.result === 'completed'
                    ? '#dcfce7'
                    : '#fee2e2';

                  return (
                    <div key={attempt.attempt} className={classes.timelineItem}>
                      <div className={classes.timelineDot} style={{ backgroundColor: resultColor }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className={classes.timelineEvent}>
                            Attempt {attempt.attempt}
                          </span>
                          <span style={{
                            display: 'inline-flex',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: resultBg,
                            color: resultColor,
                            textTransform: 'capitalize'
                          }}>
                            {attempt.result}
                          </span>
                        </div>
                        <span className={classes.timelineTime}>
                          Agent: {attempt.agent_username} (Ext: {attempt.extension})
                        </span>
                        <span className={classes.timelineTime}>
                          {moment(attempt.started_at).format(globalDateFormat)} → {moment(attempt.ended_at).format(globalDateFormat)}
                        </span>
                        <span className={classes.timelineTime}>
                          Duration: {formatDuration(attempt.duration_sec)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Events Timeline */}
          {detailsData.events && detailsData.events.length > 0 && (
            <div className={classes.section}>
              <span className={classes.sectionTitle}>Call Timeline</span>
              <div className={classes.timeline}>
                {(() => {
                  const events = detailsData.events;
                  const lastIndex = events.length - 1;

                  // Find all canceled event indices
                  const canceledIndices = events
                    .map((e, i) => e.event_type === 'canceled' ? i : -1)
                    .filter(i => i !== -1);

                  const lastCanceledIndex = canceledIndices.length > 0
                    ? canceledIndices[canceledIndices.length - 1]
                    : -1;

                  return events.map((event, index) => {
                    let displayType = event.event_type.replace(/_/g, ' ');
                    let dotColor = theme.palette.secondary.value || theme.palette.primary.main;

                    if (event.event_type === 'canceled') {
                      if (index === lastCanceledIndex) {
                        displayType = 'Canceled';
                        dotColor = '#991b1b';
                      } else {
                        displayType = 'Missed';
                        dotColor = '#991b1b';
                      }
                    } else if (event.event_type === 'answered') {
                      dotColor = '#166534';
                    } else if (event.event_type === 'ended') {
                      dotColor = '#6B7280';
                    }

                    return (
                      <div key={event.id} className={classes.timelineItem}>
                        <div className={classes.timelineDot} style={{ backgroundColor: dotColor }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span className={classes.timelineEvent} style={{
                            color: event.event_type === 'canceled' ? '#991b1b' : theme.palette.text.primary
                          }}>
                            {displayType}
                          </span>
                          <span className={classes.timelineTime}>
                            {moment(event.occurred_at).format(globalDateFormat)}
                          </span>
                          {event.agent_username && (
                            <span className={classes.timelineTime}>
                              Agent: {event.agent_username} (Ext: {event.extension})
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const detailsActions = (
    <div className={classes.modalActions}>
      <ButtonOmni onClick={handleDetailsClose} variant="outlined" label="Close" />
    </div>
  );

  return (
    <FusePageSimple
      header={
        <div className={classes.headerWrapper}>
          <div className={classes.headerLeft}>
            <FuseAnimate animation="transition.expandIn" delay={300}>
              <PhoneInTalkIcon fontSize="large" className={classes.headerIcon} />
            </FuseAnimate>
            <FuseAnimate animation="transition.slideLeftIn" delay={300}>
              <Typography variant="h6" className={classes.headerTitle}>
                Call Center Report
              </Typography>
            </FuseAnimate>
          </div>
        </div>
      }
      content={
        <div className={classes.content}>
          <DateFilterWithExport
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            showDateRange={true}
            showExport={true}
            onExport={onExport}
            isExporting={isExporting}
            filterFields={filterFields}
            filterValues={filterValues}
            onFiltersApply={handleFiltersApply}
            onFiltersClear={handleFiltersClear}
            showRefresh={true}
            onRefresh={handleRefresh}
          />

          <TableOmniNew
            rows={data}
            headCells={headCells}
            perPageRows={currentParams.limit}
            totalItems={totalItems}
            curPage={currentParams.page}
            setCurrentParams={setCurrentParams}
            loading={isLoading}
            isLoadingPagination={false}
            fixedHeight={true}
            maxTableHeight="600px"
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            onViewClick={handleViewClick}
          />

          <ModalOmni
            open={detailsOpen}
            handleClose={handleDetailsClose}
            title="Call Details"
            content={detailsContent}
            actions={detailsActions}
            customWidth={750}
          />

          <AudioPlayerModal
            open={recordingOpen}
            onClose={() => setRecordingOpen(false)}
            audioUrl={recordingUrl}
            callId={recordingCallId}
            onDownload={handleDownloadRecording}
          />

          <SnackbarOmni {...snackbar} />
        </div>
      }
    />
  );
}

export default React.memo(CallCenterReport);